import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { emit, snapshotCart } from "@/lib/events";

const CartContext = createContext(null);
const KEY = "siyaara_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [packaging, setPackagingState] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY + "_packaging")) || null;
    } catch {
      return null;
    }
  });
  const setPackaging = (p) => {
    setPackagingState(p);
    try {
      localStorage.setItem(KEY + "_packaging", JSON.stringify(p));
    } catch { /* private mode */ }
  };

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
    snapshotCart(items, items.reduce((s, i) => s + i.locked_price * i.qty, 0));
  }, [items]);

  // personalisation: {selections: {optId: value}, summary: string, surcharge: number} | null
  // design: coin motif choice (validated server-side against available_designs)
  const addItem = (product, variant, qty = 1, personalisation = null, design = "", size = "") => {
    const pricing = variant.pricing || {};
    const persSurcharge = personalisation?.surcharge || 0;
    const gstRate = product.rate_info?.gst_rate ?? 3;
    // final unit price incl. personalisation surcharge (taxed at gst_rate)
    const base = pricing.subtotal ?? 0;
    const subWithPers = base + persSurcharge;
    const unitPrice = pricing.overridden
      ? (pricing.final || 0)
      : Math.round((subWithPers + (subWithPers * gstRate) / 100) * 100) / 100;
    const persKey = personalisation?.summary ? personalisation.summary : "";
    const key = `${product.id}:${variant.id}:${JSON.stringify(variant.options || {})}:${persKey}:${design}:${size}`;
    emit("add_to_cart", {
      value: (variant.pricing?.display_price || 0) * qty, currency: "INR",
      items: [{ item_id: variant.sku || product.slug, item_name: product.title, category: product.category,
                metal: product.metal, purity: variant.purity || product.purity,
                price: variant.pricing?.display_price || 0, quantity: qty }],
    });
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, qty: Math.min(i.qty + qty, variant.stock) } : i));
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          variant_id: variant.id,
          options: variant.options || {},
          slug: product.slug,
          title: product.title,
          variant_label:
            Object.entries(variant.options || {}).map(([k, v]) => `${k}: ${v}`).join(" / ") || "Standard",
          sku: variant.sku,
          metal: variant.metal || product.metal,
          purity: variant.purity || product.purity,
          net_weight: variant.net_weight,
          image: (product.images || [])[0] || "",
          stock: variant.stock,
          qty: Math.min(qty, variant.stock),
          locked_price: unitPrice,
          locked_at: new Date().toISOString(),
          rate_id: product.rate_info?.rate_id || "",
          personalisation,
          design: design || "",
          size: size || "",
          tier: variant._tier || "",
          lead_time: variant._lead || "",
          carats: (variant.stones || []).reduce((s, st) => s + (st.weight_carat || 0), 0),
        },
      ];
    });
    toast.success("Added to cart", { description: product.title });
    setDrawerOpen(true);
  };

  const updateQty = (key, qty) => {
    setItems((prev) =>
      qty <= 0
        ? prev.filter((i) => i.key !== key)
        : prev.map((i) => (i.key === key ? { ...i, qty: Math.min(qty, i.stock || 99) } : i))
    );
  };

  const toggleItemGift = (key) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, gift_wrap: !i.gift_wrap } : i)));

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const clear = () => {
    setItems([]);
    setCoupon(null);
    setPackaging(null);
  };
  const refreshPrices = (changes) => {
    // After a 409 reprice: adopt the new prices as fresh locks
    setItems((prev) =>
      prev.map((i) => {
        const change = changes.find(
          (c) => c.product_id === i.product_id && c.variant_id === i.variant_id
        );
        return change
          ? { ...i, locked_price: change.new, locked_at: new Date().toISOString() }
          : i;
      })
    );
  };

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.locked_price * i.qty, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);
  const hasPersonalised = useMemo(() => items.some((i) => i.personalisation), [items]);

  return (
    <CartContext.Provider
      value={{
        items, addItem, updateQty, removeItem, clear, refreshPrices, toggleItemGift,
        subtotal, count, hasPersonalised, drawerOpen, setDrawerOpen, coupon, setCoupon,
        packaging, setPackaging,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
